import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
  MessageSquare, FileText, ShieldCheck, Lock, Send, CheckCircle2,
  CalendarClock, Video, Users2, AlertCircle, Sparkles, PhoneOff, Radio,
} from "lucide-react";

const STATUS_LABEL = {
  started: "Negotiation Started",
  awaiting_response: "Awaiting Response",
  terms_agreed: "Terms Agreed",
  contract_accepted: "Contract Accepted",
  deployment_confirmed: "Deployment Confirmed",
  completed: "Job Completed",
  reviewed: "Performance Reviewed",
};

const CONTRACT_FIELDS = [
  { k: "hourly_rate", l: "Hourly rate (€)", type: "number" },
  { k: "daily_rate", l: "Daily rate (€)", type: "number" },
  { k: "expenses", l: "Expenses" },
  { k: "travel_allowance", l: "Travel allowance" },
  { k: "accommodation", l: "Accommodation" },
  { k: "uniform_requirements", l: "Uniform requirements" },
  { k: "shift_pattern", l: "Shift pattern" },
  { k: "start_time", l: "Start time" },
  { k: "finish_time", l: "Finish time" },
  { k: "cancellation_terms", l: "Cancellation terms", area: true },
  { k: "special_instructions", l: "Special instructions", area: true },
];

function StatusPipeline({ status }) {
  const order = Object.keys(STATUS_LABEL);
  const idx = Math.max(0, order.indexOf(status));
  return (
    <div className="card-flat p-4">
      <div className="flex flex-wrap items-center gap-2 text-[11px] font-outfit font-bold uppercase tracking-widest" data-testid="negot-status-pipeline">
        {order.map((s, i) => (
          <React.Fragment key={s}>
            <span className={`px-2 py-1 rounded ${i <= idx ? "bg-brand text-white" : "bg-[#141414] text-white/50"}`} data-testid={`negot-step-${s}`}>
              {STATUS_LABEL[s]}
            </span>
            {i < order.length - 1 && <span className="text-slate-300">→</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default function NegotiationRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [contractDraft, setContractDraft] = useState({});
  const [contractNote, setContractNote] = useState("");
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const load = async (silent = false) => {
    try {
      const { data } = await api.get(`/negotiations/${id}`);
      setData(data);
      if (data.contract?.terms) setContractDraft((d) => Object.keys(d).length ? d : data.contract.terms);
      if (!silent) scrollToBottom();
    } catch (e) { if (!silent) toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); pollRef.current = setInterval(() => load(true), 6000); return () => clearInterval(pollRef.current); /* eslint-disable-next-line */ }, [id]);

  const scrollToBottom = () => setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 100);

  const sendMessage = async () => {
    const body = msg.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/negotiations/${id}/messages`, { body });
      setMsg("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveContract = async () => {
    setBusy(true);
    try {
      await api.post(`/negotiations/${id}/contract`, { terms: contractDraft, note: contractNote });
      setContractNote("");
      toast.success("Contract version saved. Any previous signatures were reset.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sign = async () => {
    if (!data?.contract) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/negotiations/${id}/sign`, { version_no: data.contract.version_no, accept: true });
      toast.success(res.both_signed ? "Both parties have signed. Contract locked." : "You've signed. Awaiting the other party.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const advance = async (next) => {
    try {
      await api.post(`/negotiations/${id}/status`, { status: next });
      await load();
      toast.success(`Status → ${STATUS_LABEL[next]}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const signedByMe = useMemo(() => {
    if (!data || !user) return false;
    return (data.signatures || []).some(s => s.signer_id === user.id && !s.invalidated && s.contract_version === data.contract?.version_no);
  }, [data, user]);

  if (!data) return <div className="max-w-5xl mx-auto px-4 py-12 text-white/60 font-manrope">Loading room…</div>;
  const contractLocked = data.contract?.locked;
  const canAdvance = data.room?.status === "contract_accepted" && (user?.role === "company" || user?.role === "super_admin");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-5">
      {/* Header */}
      <div className="card-flat p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Secure negotiation room · {data.room.id.slice(0, 8)}</div>
            <div className="font-outfit font-bold text-white text-xl mt-1 truncate">{data.room.subject}</div>
            <div className="text-sm text-white/60 font-manrope mt-1">
              All communications stay inside SecuSource — no personal phone/email is exchanged until both parties sign.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <VideoCallButton roomId={id} />
            {canAdvance && (
              <>
                <Button onClick={() => advance("deployment_confirmed")} className="bg-gold-500 hover:bg-gold-600 text-white" data-testid="negot-confirm-deploy">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm deployment
                </Button>
              </>
            )}
            {data.room.status === "deployment_confirmed" && (
              <Button onClick={() => advance("completed")} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="negot-mark-done">
                <Sparkles className="w-4 h-4 mr-2" /> Mark completed
              </Button>
            )}
          </div>
        </div>
      </div>

      <StatusPipeline status={data.room.status} />

      <Tabs defaultValue="chat">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="chat" data-testid="negot-tab-chat"><MessageSquare className="w-3.5 h-3.5 mr-1" /> Chat</TabsTrigger>
          <TabsTrigger value="contract" data-testid="negot-tab-contract"><FileText className="w-3.5 h-3.5 mr-1" /> Contract</TabsTrigger>
          <TabsTrigger value="agreement" data-testid="negot-tab-agreement"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Agreement</TabsTrigger>
        </TabsList>

        {/* Chat */}
        <TabsContent value="chat">
          <div className="card-flat p-0 overflow-hidden">
            <div ref={scrollRef} className="p-4 space-y-3 min-h-[380px] max-h-[500px] overflow-y-auto bg-[#0E0E0E]/50" data-testid="negot-messages">
              {(data.messages || []).length === 0 && <div className="text-center text-sm text-white/60 font-manrope py-12">No messages yet — start the conversation below.</div>}
              {(data.messages || []).map((m) => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm font-manrope ${mine ? "bg-brand text-white" : "bg-[#0A0A0A] border border-white/10 text-white"}`}>
                      <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{m.sender_role.replace(/_/g, " ")} · {new Date(m.sent_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                      <div className="whitespace-pre-wrap">{m.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-white/10 p-3 flex gap-2 bg-[#0A0A0A]">
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()} placeholder="Type a message…" data-testid="negot-msg-input" />
              <Button onClick={sendMessage} disabled={busy || !msg.trim()} className="bg-brand hover:bg-brand-hover text-white" data-testid="negot-send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Contract */}
        <TabsContent value="contract">
          <div className="card-flat p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Current terms · v{data.contract?.version_no || 1}</div>
                <div className="text-xs text-white/60 font-manrope">Last edited by {data.contract?.editor_role?.replace(/_/g, " ") || "—"} · {data.contract?.created_at ? new Date(data.contract.created_at).toLocaleString() : "—"}</div>
              </div>
              {contractLocked && <span className="verified-chip"><Lock className="w-3.5 h-3.5" /> Locked</span>}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {CONTRACT_FIELDS.map(f => (
                <div key={f.k} className={f.area ? "md:col-span-2" : ""}>
                  <Label>{f.l}</Label>
                  {f.area ? (
                    <Textarea rows={2} disabled={contractLocked} value={contractDraft[f.k] || ""} onChange={(e) => setContractDraft({ ...contractDraft, [f.k]: e.target.value })} data-testid={`contract-${f.k}`} />
                  ) : (
                    <Input type={f.type || "text"} disabled={contractLocked} value={contractDraft[f.k] ?? ""} onChange={(e) => setContractDraft({ ...contractDraft, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`contract-${f.k}`} />
                  )}
                </div>
              ))}
            </div>
            {!contractLocked && (
              <>
                <Textarea rows={2} value={contractNote} onChange={(e) => setContractNote(e.target.value)} placeholder="Change note (optional) — e.g., 'Raised hourly rate + added dashcam requirement'" data-testid="contract-note" />
                <Button onClick={saveContract} disabled={busy} className="bg-brand hover:bg-brand-hover text-white font-manrope font-semibold" data-testid="contract-save">
                  Save new version
                </Button>
                <div className="text-xs text-white/60 font-manrope"><AlertCircle className="w-3.5 h-3.5 inline mr-1 text-amber-500" />Saving a new version invalidates prior signatures — both parties must re-sign.</div>
              </>
            )}
            {data.contract_history?.length > 1 && (
              <div className="border-t border-white/10 pt-3">
                <div className="text-xs uppercase tracking-widest text-white/60 font-outfit font-bold mb-2">Version history</div>
                <div className="space-y-1 text-xs font-manrope text-white/60">
                  {data.contract_history.slice().reverse().map(v => (
                    <div key={v.id}>v{v.version_no} · {v.editor_role?.replace(/_/g, " ")} · {new Date(v.created_at).toLocaleString()}{v.note ? ` — ${v.note}` : ""}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Agreement */}
        <TabsContent value="agreement">
          <div className="card-flat p-5 space-y-4">
            {contractLocked ? (
              <>
                <div className="text-center py-4">
                  <CheckCircle2 className="w-10 h-10 text-gold-500 mx-auto mb-3" />
                  <div className="font-outfit font-bold text-white text-lg">Contract locked — both parties have signed.</div>
                  <div className="text-sm text-white/60 font-manrope mt-2">Any further changes require a new negotiation.</div>
                </div>

                <DeploymentConfirmation roomId={id} data={data} onDone={load} />

                <Button
                  onClick={() => {
                    const token = localStorage.getItem("ss_token") || "";
                    const base = process.env.REACT_APP_BACKEND_URL || "";
                    window.open(`${base}/api/negotiations/${id}/pdf?auth=${encodeURIComponent(token)}`, "_blank", "noopener,noreferrer");
                  }}
                  className="w-full bg-navy-900 hover:bg-navy-800 text-white py-5 font-manrope font-semibold"
                  data-testid="agreement-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> Download signed contract + full negotiation transcript (PDF)
                </Button>
              </>
            ) : (
              <>
                <div className="text-sm font-manrope text-white/70">
                  Signing accepts the terms as they stand in <strong>v{data.contract?.version_no}</strong>. If the other party later edits the contract, your signature is invalidated automatically.
                </div>
                <Button onClick={sign} disabled={busy || signedByMe || user?.role === "super_admin"} className={`w-full py-6 font-manrope font-semibold ${signedByMe ? "bg-gold-500 hover:bg-gold-500" : "bg-brand hover:bg-brand-hover"} text-white`} data-testid="agreement-sign-btn">
                  <ShieldCheck className="w-4 h-4 mr-2" /> {signedByMe ? "You've signed — waiting on other party" : "Accept terms & sign"}
                </Button>
              </>
            )}
            <div className="border-t border-white/10 pt-3">
              <div className="text-xs uppercase tracking-widest text-white/60 font-outfit font-bold mb-2">Audit trail</div>
              {data.signatures.length === 0 ? (
                <div className="text-xs text-white/60 font-manrope">No signatures on file yet.</div>
              ) : (
                <div className="space-y-1 text-xs font-manrope text-white/60">
                  {data.signatures.map(s => (
                    <div key={s.id} className={s.invalidated ? "line-through" : ""} data-testid={`signature-${s.id}`}>
                      <ShieldCheck className={`w-3 h-3 inline mr-1 ${s.invalidated ? "text-white/50" : "text-gold-500"}`} />
                      v{s.contract_version} · {s.signer_role.replace(/_/g, " ")} · {new Date(s.signed_at).toLocaleString()} · IP {s.ip}
                      {s.invalidated ? " (superseded)" : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * DeploymentConfirmation — pro/team-member confirms they'll actually deploy
 * ------------------------------------------------------------------------- */
function DeploymentConfirmation({ roomId, data, onDone }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  if (!user || (user.role !== "security_professional" && user.role !== "lead_professional")) {
    // Company / admin — show the roster of who has confirmed
    const confs = data?.deployment_confirmations || [];
    return (
      <div className="rounded-lg border border-white/10 bg-[#0E0E0E]/60 p-4" data-testid="deployment-roster">
        <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Deployment roster</div>
        {confs.length === 0 ? (
          <div className="text-sm text-white/60 font-manrope">No professionals have confirmed deployment yet.</div>
        ) : (
          <div className="space-y-1 text-sm font-manrope">
            {confs.map((c) => (
              <div key={c.user_id} className="flex items-center gap-2" data-testid={`roster-${c.secusource_id}`}>
                {c.confirmed ? <CheckCircle2 className="w-4 h-4 text-gold-500" /> : <AlertCircle className="w-4 h-4 text-red-500" />}
                <strong className="text-white">{c.secusource_id}</strong>
                <span className="text-white/60">{c.name}</span>
                <span className={c.confirmed ? "text-gold-500" : "text-red-600"}>· {c.confirmed ? "Confirmed" : "Declined"}</span>
                <span className="text-white/50 text-xs">· {new Date(c.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const myConf = (data?.deployment_confirmations || []).find((c) => c.user_id === user.id);

  const submit = async (confirmed) => {
    setBusy(true);
    try {
      await api.post(`/negotiations/${roomId}/deployment-confirm`, { confirmed, note });
      toast.success(confirmed ? "Deployment confirmed" : "Deployment declined — company notified.");
      setNote("");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-brand/40 bg-brand-light/40 p-4" data-testid="deployment-confirm">
      <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2 flex items-center gap-1">
        <CheckCircle2 className="w-3.5 h-3.5" /> Confirm your deployment
      </div>
      <div className="text-sm text-white/85 font-manrope mb-3">
        Please confirm you will personally deploy on the agreed date &amp; location. The company sees your confirmation live. Declining releases the slot so the team can find a replacement early.
      </div>
      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (e.g., 'travelling from Cork, will arrive 07:30')" data-testid="deployment-note" />
      <div className="flex flex-wrap gap-2 mt-3">
        <Button onClick={() => submit(true)} disabled={busy} className="bg-gold-500 hover:bg-gold-600 text-white font-manrope font-semibold" data-testid="deployment-confirm-yes">
          <CheckCircle2 className="w-4 h-4 mr-2" /> I&apos;ll be there — confirm deployment
        </Button>
        <Button onClick={() => submit(false)} disabled={busy} variant="outline" className="border-red-500 text-red-600 hover:bg-red-50 font-manrope font-semibold" data-testid="deployment-confirm-no">
          <AlertCircle className="w-4 h-4 mr-2" /> I can&apos;t make it — decline
        </Button>
      </div>
      {myConf && (
        <div className={`mt-3 text-xs font-manrope ${myConf.confirmed ? "text-gold-600" : "text-red-700"}`}>
          You {myConf.confirmed ? "confirmed" : "declined"} on {new Date(myConf.at).toLocaleString()}. You can change your answer if plans change.
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * VideoCallButton — Daily.co powered in-room video interview
 * ------------------------------------------------------------------------- */
function VideoCallButton({ roomId }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const iframeContainerRef = useRef(null);
  const callFrameRef = useRef(null);
  const negotJoinedRef = useRef(false);

  const startCall = async () => {
    setBusy(true);
    setErrMsg("");
    try {
      const { data } = await api.post(`/negotiations/${roomId}/video/session`);
      setSession(data);
      setOpen(true);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErrMsg(msg);
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let localFrame = null;
    if (!open || !session?.room_url || !session?.token) return;
    (async () => {
      try {
        const DailyIframe = (await import("@daily-co/daily-js")).default;
        if (cancelled || !iframeContainerRef.current) return;
        // Kill any dangling global Daily instance (React StrictMode / previous modal)
        try {
          const existing = DailyIframe.getCallInstance && DailyIframe.getCallInstance();
          if (existing) {
            try { await existing.leave(); } catch (_e1) { /* noop */ }
            try { existing.destroy(); } catch (_e2) { /* noop */ }
          }
        } catch (_e3) { /* noop */ }
        if (cancelled || !iframeContainerRef.current) return;
        try { iframeContainerRef.current.innerHTML = ""; } catch (_e4) { /* noop */ }
        localFrame = DailyIframe.createFrame(iframeContainerRef.current, {
          showLeaveButton: true,
          iframeStyle: { width: "100%", height: "100%", border: "0", borderRadius: "8px" },
        });
        callFrameRef.current = localFrame;
        // Only auto-close AFTER a successful join
        localFrame.on("left-meeting", () => { if (negotJoinedRef.current) setOpen(false); });
        localFrame.on("error", (ev) => {
          setErrMsg("Daily.co error: " + (ev?.errorMsg || JSON.stringify(ev || {})));
        });
        await localFrame.join({ url: session.room_url, token: session.token });
        if (!cancelled) negotJoinedRef.current = true;
      } catch (e) {
        setErrMsg("Failed to load video call: " + (e?.message || e?.errorMsg || "unknown error"));
      }
    })();
    return () => {
      cancelled = true;
      negotJoinedRef.current = false;
      if (localFrame) {
        try { localFrame.leave(); } catch (_e) { /* noop */ }
        try { localFrame.destroy(); } catch (_e) { /* noop */ }
      }
      if (callFrameRef.current === localFrame) callFrameRef.current = null;
    };
  }, [open, session]);

  const handleClose = (v) => {
    if (!v && callFrameRef.current) {
      try { callFrameRef.current.leave(); } catch (_) { /* noop */ }
      try { callFrameRef.current.destroy(); } catch (_) { /* noop */ }
      callFrameRef.current = null;
    }
    setOpen(v);
    if (!v) setSession(null);
  };

  return (
    <>
      <Button
        onClick={startCall}
        disabled={busy}
        variant="outline"
        className="border-brand text-brand hover:bg-brand/5 font-manrope font-semibold"
        data-testid="negot-video-btn"
      >
        <Video className="w-4 h-4 mr-2" /> {busy ? "Starting…" : "Start video call"}
      </Button>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 overflow-hidden" data-testid="video-call-dialog">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2 font-outfit">
              <Video className="w-5 h-5 text-brand" /> Secure video call
              {session?.is_owner && (
                <span className="ml-2 text-[10px] uppercase tracking-widest bg-gold-100 text-gold-600 px-2 py-0.5 rounded-full font-outfit font-bold flex items-center gap-1">
                  <Radio className="w-3 h-3" /> Host · can record
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {errMsg ? (
            <div className="px-5 pb-5">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm font-manrope text-amber-900 flex gap-3" data-testid="video-error">
                <AlertCircle className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
                <div>
                  <div className="font-outfit font-bold mb-1">Video call unavailable</div>
                  <div className="text-amber-800">{errMsg}</div>
                  <div className="mt-2 text-xs text-amber-700">
                    Chat and contract signing continue to work normally in the meantime.
                  </div>
                </div>
              </div>
              <Button onClick={() => handleClose(false)} className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white font-manrope">
                <PhoneOff className="w-4 h-4 mr-2" /> Close
              </Button>
            </div>
          ) : (
            <div
              ref={iframeContainerRef}
              className="w-full bg-slate-900"
              style={{ height: "min(70vh, 600px)" }}
              data-testid="video-frame-container"
            />
          )}
          <div className="px-5 pb-4 text-[11px] text-white/60 font-manrope border-t border-white/10 pt-3">
            <ShieldCheck className="w-3 h-3 inline mr-1 text-gold-500" />
            Encrypted video · Both parties must grant camera/mic permission · Recording (when host-enabled) stored securely for post-call playback.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
